import React from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter as Router,
  Route,
  Link
} from 'react-router-dom'

import { AppContainer } from 'react-hot-loader';

// settings + utils
import Settings from 'config.js';
import API from 'utils/API.js';
import CSV from 'utils/CSV.js';
import Auth from 'services/Auth.js';
import XHR from 'components/lib/swell-ui-0.0.3/utils/XHR';

// base components
import Spinner from 'components/shared/Spinner';
import LoginForm from 'components/LoginForm';
import Layout from 'pages/shared/Layout';

// main
import EducationalMainPage from 'pages/home/EducationalMainPage';

// kits
import KitEditorPage from 'pages/kits/KitEditorPage';

// orders
import KitsOverviewPage from 'pages/kits/KitsOverviewPage';
import KitOrdersOverviewPage from 'pages/kit-orders/KitOrdersOverviewPage';
import KitOrdersEditorPage from 'pages/kit-orders/KitOrdersEditorPage';
import KitProblemsPage from 'pages/kits/KitProblemsPage';
import ShippingPage from 'pages/kit-orders/ShippingPage';
import CopyPastePage from 'pages/kit-orders/CopyPastePage';

// users
import TeacherOverviewPage from 'pages/teachers/TeacherOverviewPage';
import TeacherViewerPage from 'pages/teachers/TeacherViewerPage';

// schools
import SchoolsOverviewPage from 'pages/schools/SchoolsOverviewPage';
import SchoolViewerPage from 'pages/schools/SchoolViewerPage';
import SchoolEditorPage from 'pages/schools/SchoolEditorPage';

// principals
import PrincipalsOverviewPage from 'pages/principals/PrincipalsOverviewPage';
import PrincipalViewerPage from 'pages/principals/PrincipalViewerPage';
import PrincipalEditorPage from 'pages/principals/PrincipalEditorPage';

// marketing/mailing lists
import ListOverviewPage from 'pages/marketing/recipients/ListOverviewPage';
import ListEditorPage from 'pages/marketing/recipients/ListEditorPage';
import ListViewerPage from 'pages/marketing/recipients/ListViewerPage';

// marketing/mailing queues
import MailingOverviewPage from 'pages/marketing/mailings/MailingOverviewPage';
import MailingEditorPage from 'pages/marketing/mailings/MailingEditorPage';
import MailingViewerPage from 'pages/marketing/mailings/MailingViewerPage';

// marketing/surveys
import SurveyOverviewPage from 'pages/marketing/surveys/SurveyOverviewPage';
import SurveyEditorPage from 'pages/marketing/surveys/SurveyEditorPage';
import SurveyViewerPage from 'pages/marketing/surveys/SurveyViewerPage';
import SurveyResultsPage from 'pages/marketing/surveys/SurveyResultsPage';

// tools
import MIURToolPage from 'pages/tools/MIURToolPage';

// etc.
import ClientProjectsPage from 'pages/etc/projects/ClientProjectsPage';

const Cache = {
  set: (key, value) => {

  },
  get: (key) => {

  }
};

class App extends React.Component {
  constructor(props) {
    super(props);

    this.REFRESH_REPORT_SCHEDULE = 1000 * 60 * 5; // refresh report every x milliseconds

    this.state = {
      queue: {},
      fetchingUser: false,
      fetchingUserError: '',
      fetchingLocations: false,
      fetchingLocationsError: '',
      fetchingProducts: false,
      fetchingProductsError: '',
      fetchingSchoolclasses: false,
      fetchingSchoolclassesError: '',
      fetchingKitRequestReport: false,
      fetchingKitRequestReportError: '',
      fetchingSchools: false,
      fetchingSchoolsError: '',

      currUser: null,
      loginFormError: '',

      appData: {
        // caches, basically
        cache: {
          kitGroups: [],
          kitGroupsById: {},
          kits: [],
          kitsById: {},
          kitsByGroup: {},
          locationsTree: [],
          locationsById: {},
          locationsByType: {
            region: [],
            province: [],
            city: []
          },
          schoolclasses: [],
          schoolclassesGroups: [],
          schoolclassesById: {},

          reports: {
            kitRequests: null
          },

          schools: [],
          schoolsById: {}
        },

        // mailing queue to create recipient list
        mailings: {
          mailingPanelList: {
            schoolsById: {},
            teachersById: {}
          }
        }
      },
    };

    this.getCurrUser = this.getCurrUser.bind(this);

    this.fetchKits = this.fetchKits.bind(this);
    this.fetchLocations = this.fetchLocations.bind(this);
    this.fetchSchoolclasses = this.fetchSchoolclasses.bind(this);
    this.fetchOrdersReport = this.fetchOrdersReport.bind(this);
    this.fetchSchools = this.fetchSchools.bind(this);

    this.handleLoginFormSubmit = this.handleLoginFormSubmit.bind(this);

    this.handleRecipientListUpdate = this.handleRecipientListUpdate.bind(this);
    this.handleRemoveAllRecipients = this.handleRemoveAllRecipients.bind(this);
    this.handleAfterRecipientListSave = this.handleAfterRecipientListSave.bind(this);
  }

  componentWillMount() {
    // handle auth
    this.getCurrUser((user) => {
      this.setState({
        currUser: user
      });
    });

    this.fetchKits();
    this.fetchLocations();
    this.fetchSchoolclasses();
    this.fetchOrdersReport(true);
    this.fetchSchools();
  }

  // handle products + kits
  fetchKits() {
    this.setState({
      fetchingProducts: true,
      fetchingProductsError: ''
    }, () => {
      API.call('GET', Settings.V3_API_URL + '/kit-groups?offset=0&limit=-1&access_token=' + Auth.getToken(), {}, (response, error) => {
        if (error || !response.data || !response.data.groups) {
          this.setState({
            fetchingProducts: false,
            fetchingProductsError: 'Cannot fetch groups' + (error ? ': ' + error.message : ''),

            error: 'Error getting groups' + (error ? ': ' + error.message : '')
          });

          return false;
        }

        this.setState((prevState) => {
          prevState.appData.cache.kitGroups = response.data.groups;

          response.data.groups.forEach((group) => {
            prevState.appData.cache.kitGroupsById[group.id] = group;
          });

          return prevState;
        });

        // get kits
        API.call('GET', Settings.V3_API_URL + '/kits?offset=0&limit=-1&access_token=' + Auth.getToken(), {}, (response, error) => {
          if (error || !response.data || !response.data.kits) {
            this.setState({
              fetchingProducts: false,
              fetchingProductsError: 'Cannot fetch kits' + (error ? ': ' + error.message : ''),

              error: 'Error getting kits' + (error ? ': ' + error.message : '')
            });

            return false;
          }

          const kits = response.data.kits;
          let kitsByGroup = {};
          let kitsById = {};

          kits.forEach((myProduct) => {
            if (!kitsByGroup[myProduct.group.id]) {
              kitsByGroup[myProduct.group.id] = [];
            }
            kitsByGroup[myProduct.group.id].push(myProduct);

            kitsById[myProduct.id] = (myProduct);
          });

          this.setState((prevState) => {
            prevState.fetchingProducts = false;

            prevState.appData.cache.kits = kits;
            prevState.appData.cache.kitsById = kitsById;
            prevState.appData.cache.kitsByGroup = kitsByGroup;

            return prevState;
          });
        });
      });
    });
  }

  fetchLocations() {
    console.log('[FETCH] handle locations...');

    const ENABLE_CACHE = false;

    const LOCATIONS_CACHE_KEY = 'colibri-libri-dashboard:appCache:locations-' + ((new Date()).getFullYear() + '-' + ((new Date()).getMonth() + 1) + '-' + ((new Date()).getDate()));

    if (ENABLE_CACHE && window.localStorage.getItem(LOCATIONS_CACHE_KEY)) {
      const locationData = JSON.parse(window.localStorage.getItem(LOCATIONS_CACHE_KEY));
      if (!locationData) {
        alert('no location data');
      }

      console.log('[CACHE] locations from cache');

      this.setState((prevState) => {
        prevState.appData.cache.locationsById = locationData.locationsById;
        prevState.appData.cache.locationsByType = locationData.locationsByType;
        prevState.appData.cache.locationsTree = locationData.locationsTree;
      });
    } else {
      // get locations
      this.setState({
        fetchingLocations: true,
        fetchingLocationsError: ''
      }, () => {
        ['region', 'province', 'city'].forEach((type) => {
          console.log('Fetch locations: ' + type + '...');

          let endpoint = Settings.LOCATIONS_API_URL + '/locations?type=' + type + '&access_token=' + Settings.LOCATIONS_API_ACCESS_TOKEN + '&t=' + (new Date).getTime();

          let controller = XHR.fetch('GET', endpoint, {}, {}, (response, error) => {
            if (error) {
              this.setState((prevState) => {
                delete prevState.queue['locations_' + type];

                prevState.error = error.message + ' while gettings locations of type ' + type;

                console.error('ERROR getting locations: ' + error.message);

                return prevState;
              });

              return false;
            }

            if (!response.locations) {
              console.error('cannot get locations');
              console.dir(response);

              return false;
            }

            let locationsById = this.state.appData.cache.locationsById;
            let locationsByType = this.state.appData.cache.locationsByType;
            let locationsTree = this.state.appData.cache.locationsTree;

            // add by ID
            response.locations.forEach((myLocation) => {
              locationsById[myLocation.id] = myLocation;
            });

            // add by type
            locationsByType[type] = response.locations;

            // build tree
            if (type == 'city') {
              response.locations.forEach((myLocation) => {
                let regionIndex;
                let provinceIndex;
                let cityIndex;

                regionIndex = locationsTree.findIndex(x => x.id == myLocation.region.id);
                if (regionIndex === -1) {
                  const o = Object.assign(myLocation.region, {provinces: []});
                  locationsTree.push(o);
                  regionIndex = locationsTree.length - 1;
                }

                provinceIndex = locationsTree[regionIndex].provinces.findIndex(x => x.id == myLocation.province.id);
                if (provinceIndex === -1) {
                  const o = Object.assign(myLocation.province, {cities: []});
                  locationsTree[regionIndex].provinces.push(o);
                  provinceIndex = locationsTree[regionIndex].provinces.length - 1;
                }

                locationsTree[regionIndex].provinces[provinceIndex].cities.push(myLocation);
              });
            }

            // figure out "fetching" state
            const doneFetchingLocations = locationsByType['region'].length && locationsByType['province'].length && locationsByType['city'].length;
            if (doneFetchingLocations) {
              // must sort tree
              locationsTree.sort((a, b) => {
                return a.name > b.name ? 1 : -1;
              });
              locationsTree.forEach((region, i) => {
                region.provinces.sort((a, b) => {
                  return a.name > b.name ? 1 : -1;
                });

                region.provinces.forEach((province, j) => {
                  province.cities.sort((a, b) => {
                    return a.name > b.name ? 1 : -1;
                  });
                })
              });
            }

            console.log('444444444444444444');
            console.dir(doneFetchingLocations);
            console.log('444444444444444444');

            this.setState((prevState) => {
              delete prevState.queue['locations_' + type];

              prevState.appData.cache.locationsById = locationsById;
              prevState.appData.cache.locationsByType = locationsByType;
              prevState.appData.cache.locationsTree = locationsTree;

              prevState.fetchingLocations = doneFetchingLocations == false;

              return prevState;
            });

            const locationData = JSON.stringify(
              {
                locationsById,
                locationsByType,
                locationsTree // TODO: this is not used anywhere in the app, it works fine the first time but it doesn't get saved in localStorage (perhaps there's a depth limit? Must investigate)
              }
            );
            window.localStorage.setItem(LOCATIONS_CACHE_KEY, locationData);
          });

          this.setState((prevState) => {
            prevState.queue['locations_' + type] = controller;

            return prevState;
          });
        });
      });
    }
  }

  fetchSchoolclasses() {
    console.log('[FETCH] fetch schoolclasses...');

    // handle schoolclasses
    this.setState({
      fetchingSchoolclasses: true,
      fetchingSchoolclassesError: ''
    }, () => {
      API.call('GET', Settings.V3_API_URL + '/schoolclasses/types', {}, (response, error) => {
        if (error || !response.data || !response.data.groups) {
          this.setState({
            fetchingSchoolclasses: false,
            fetchingSchoolclassesError: 'Cannot fetch schoolclasses' + (error ? ': ' + error.message : ''),

            error: 'Error getting schoolclasses' + (error ? ': ' + error.message : '')
          });

          return false;
        }

        this.setState((prevState) => {
          prevState.fetchingSchoolclasses = false;

          prevState.appData.cache.schoolclasses = response.data.types;
          prevState.appData.cache.schoolclassesGroups = response.data.groups;

          response.data.types.forEach((schoolclass) => {
            prevState.appData.cache.schoolclassesById[schoolclass.id] = schoolclass;
          });

          return prevState;
        });
      });
    });
  }

  fetchOrdersReport(setBusy) {
    console.log('[FETCH] fetch kit request report (interval is ' + (this.REFRESH_REPORT_SCHEDULE/1000) + ' ms)...');

    // get kit request report
    this.setState({
      fetchingKitRequestReport: setBusy,
      fetchingKitRequestReportError: ''
    }, () => {
      XHR.fetch('GET', Settings.V3_API_URL + '/reports/orders/overview', {}, {
        headers: {
          Authorization: Auth.getToken() ? ('Bearer ' + Auth.getToken()) : ''
        }
      }, (response, error) => {
        if (error || !response.data || !response.data.report) {
          this.setState({
            fetchingKitRequestReport: false,
            fetchingKitRequestReportError: 'Cannot fetch report' + (error ? ': ' + error.message : ''),

            error: 'Error getting report' + (error ? ': ' + error.message : '')
          });

          return false;
        }

        this.setState((prevState) => {
          prevState.fetchingKitRequestReport = false;

          prevState.appData.cache.reports.kitRequests = response.data && response.data.report ? response.data.report : null;

          return prevState;
        });
      });
    });

    // call this function again after a while
    if (this.REFRESH_REPORT_SCHEDULE > 0) {
      window.setTimeout(() => {
        this.fetchOrdersReport(false);
      }, this.REFRESH_REPORT_SCHEDULE);
    }
  }

  fetchSchools() {
    const ENABLE_CACHE = false;

    this.setState((prevState) => {
      prevState.fetchingSchools = true;
      prevState.fetchingSchoolsError = '';

      return prevState;
    }, () => {
      let schoolCallback = (schools) => {
        let schoolsById = {};
        schools.forEach((school) => {
          schoolsById[school.id] = school;
        });

        this.setState((prevState) => {
          prevState.fetchingSchools = false;

          prevState.appData.cache.schools = schools;
          prevState.appData.cache.schoolsById = schoolsById;

          return prevState;
        });
      };

      const SCHOOLS_CACHE_KEY = 'colibri-libri-dashboard:cache:schools-' + ((new Date()).getFullYear() + '-' + ((new Date()).getMonth() + 1) + '-' + ((new Date()).getDate()));

      window.ldb.get(SCHOOLS_CACHE_KEY, (schools) => {
        if (schools && ENABLE_CACHE) {
          console.log('SCHOOLS FROM CACHE!!!', schools);

          schoolCallback(schools);
        } else {
          console.log('SCHOOLS NO CACHE, OH NO!');

          const endpoint = Settings.V3_API_URL + '/schools';
          XHR.fetch('GET', endpoint + '?offset=0&limit=-1&t=' + (new Date()).getTime(), {}, {}, (response, error) => {
            if (error) {
              this.setState((prevState) => {
                this.state.fetchingSchoolsError = error.message + ' while getting schools';

                return prevState;
              });

              return false;
            }

            if (!response.data.schools) {
              alert('Cannot get schools! (no schools in response)');

              console.error('cannot get schools');
              console.dir(response);

              return false;
            }

            // do the stuff
            const schools = response.data.schools;
            schoolCallback(schools);

            // save to cache for later use
            window.ldb.set(SCHOOLS_CACHE_KEY, schools);
          });
        }
      });
    });
  }

  getCurrUser(callback) {
    this.setState({
      fetchingUser: true,
      fetchingUserError: ''
    }, () => {
      API.call('GET', Settings.V3_API_URL + '/auth/sessions/current', {}, (response, error) => {
        this.setState({
          fetchingUser: false
        });

        if (error) {
          alert('error');
        }

        if (!response || response.ok == false) {
          if (response === null) {
            alert('no response (connection problem?)!');
          } else {
            alert('Error: ' + response.error);
          }

          Auth.setToken('');

          return callback(null);
        } else {
          const user = response.ok && response.data.user ? response.data.user : null;
          if (user) {
            if (user.user_type != 'admin') {
              alert('You are not an admin');

              return callback(null);
            }

            user.type = 'edu'; // simulate user type support

            return callback(user);
          }
        }
      });
    });
  }

  handleLoginFormSubmit(e) {
    e.preventDefault();

    this.setState({
      fetchingUser: true,
      fetchingUserError: ''
    });

    const form = e.currentTarget;
    const email = form.elements.email.value;
    const password = form.elements.password.value;

    if (!email || !password) {
      this.setState({
        loginFormError: 'Email o password mancanti'
      });

      return false;
    }

    API.call('POST', Settings.V3_API_URL + '/auth/sessions', {
      email,
      password
    }, (response) => {
      if (!response) {
        this.setState({
          loginFormError: 'Impossibile accedere: NO CONNESSIONE'
        });

        return false;
      }
      if (response && !response.ok) {
        this.setState({
          loginFormError: 'Impossibile accedere: ' + response.error
        });

        return false;
      }

      // save access token into window.localStorage so that API util can use it
      Auth.setToken(response.data.access_token);

      // load page again to refresh curr user
      this.getCurrUser((user) => {
        this.setState({
          currUser: user
        });
      });
    });

    return false;
  }

  handleRecipientListUpdate(recipients, type) {
    console.log('Update recipients of type ' + type + ' (' + Object.keys(recipients).length + ' recipients)');

    let propName = '';
    switch (type) {
      case 'schools':
        propName = 'schoolsById';
        break;
      case 'teachers':
        propName = 'teachersById';
        break;
      default:
        alert('Unknown recipient type!');

        return false;
    }

    this.setState((prevState) => {
      prevState.appData.mailings.mailingPanelList[propName] = recipients;

      return prevState;
    });
  }

  handleAfterRecipientListSave() {
    this.handleRemoveAllRecipients();
  }

  handleRemoveAllRecipients() {
    this.handleRecipientListUpdate({}, 'schools');
    this.handleRecipientListUpdate({}, 'teachers');
  }

  render() {
    const currUser = this.state.currUser;
    if (!currUser) {
      return (
        <div style={{
          position: 'absolute',
          top: '45%',
          left: '50%',
          transform: 'translate(-50%, -50%)',

          border: '0px solid red'
        }}>
          {
            this.state.fetchingUser ? (
              <div className="container mt-4 text-left">
                <i className="fa fa-user-secret animated tada infinite" style={{fontSize: '120%'}} /> Controllo utente...
              </div>
            ) : (
              <div>
                <h1><i className="fa fa-user-secret" style={{color: '#444'}} /></h1>

                <LoginForm error={this.state.loginFormError} onSubmit={this.handleLoginFormSubmit}
          />
              </div>
            )
          }
        </div>
      );
    }

    if (
      this.state.fetchingLocations ||
      this.state.fetchingProducts ||
      this.state.fetchingSchoolclasses ||
      this.state.fetchingKitRequestReport ||
      this.state.fetchingSchools
    ) {
      return (
        <div style={{
          position: 'absolute',
          top: '45%',
          left: '50%',
          transform: 'translate(-50%, -50%)',

          border: '0px solid red'
        }}>
          {
            this.state.fetchingLocations ? (
              <div className="container mt-4 text-center">
                <i className="fa fa-location-arrow animated tada infinite" style={{fontSize: '120%'}} /> Carico regioni/province/citta...
              </div>
             ) : ''
          }
          {
            this.state.fetchingProducts ? (
              <div className="container mt-4 text-center">
                <i className="fa fa-gift animated tada infinite" style={{fontSize: '120%'}} /> Carico prodotti...
              </div>
             ) : ''
          }
          {
            this.state.fetchingSchoolclasses ? (
              <div className="container mt-4 text-center">
                <i className="fa fa-graduation-cap animated tada infinite" style={{fontSize: '120%'}} /> Carico classi...
              </div>
             ) : ''
          }
          {
            this.state.fetchingKitRequestReport ? (
              <div className="container mt-4 text-center">
                <i className="fa fa-gift animated tada infinite" style={{fontSize: '120%'}} /> Carico richieste kit...
              </div>
             ) : ''
          }
          {
            this.state.fetchingSchools ? (
              <div className="container mt-4 text-center">
                <i className="fa fa-school animated tada infinite" style={{fontSize: '120%'}} /> Carico scuole...
              </div>
             ) : ''
          }
        </div>
      );
    }

    return (
      <Router>
        <Layout
          user={currUser}
          appData={this.state.appData}
          onRemoveAllRecipients={this.handleRemoveAllRecipients}
        >
          {/* Schools */}
          <Route
            exact
            path="/schools/overview"
            render={(props) => (
              <SchoolsOverviewPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
                onRecipientListUpdate={this.handleRecipientListUpdate}
              />
            )}
          />
          <Route
            path="/schools/:schoolId/view"
            render={(props) => (
              <SchoolViewerPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/schools/:schoolId/edit"
            render={(props) => (
              <SchoolEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />

          {/* Principals */}
          <Route
            exact
            path="/principals/overview"
            render={(props) => (
              <PrincipalsOverviewPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/principals/:principalId/view"
            render={(props) => (
              <PrincipalViewerPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="principals/:principalId/edit"
            render={(props) => (
              <PrincipalEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />

          {/* Teachers */}
          <Route
            exact
            path="/teachers/overview"
            render={(props) => (
              <TeacherOverviewPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
                onRecipientListUpdate={this.handleRecipientListUpdate}
              />
            )}
          />
          <Route
            path="/teachers/:teacherId/view"
            render={(props) => (
              <TeacherViewerPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />

          {/* Projects */}
          <Route
            exact
            path="/projects/overview" render=""
          />

          {/* Products */}
          <Route
            exact
            path="/kits/:kitId/edit"
            render={(props) => (
              <KitEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />

          {/* Orders */}
          <Route
            exact
            path="/kits/overview"
            render={(props) => (
              <KitsOverviewPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/kit-orders/:kitId/overview"
            render={(props) => (
              <KitOrdersOverviewPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/kit-orders/:kitId/orders/:orderId/edit"
            render={(props) => (
              <KitOrdersEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/kit-orders/:kitId/shipping"
            render={(props) => (
              <ShippingPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/kit-orders/:kitId/problems"
            render={(props) => (
              <KitProblemsPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/kit-orders/copy-paste"
            render={(props) => (
              <CopyPastePage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />

          {/* Surveys */}
          <Route
            exact
            path="/marketing/surveys/overview"
            render={(props) => (
              <SurveyOverviewPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            exact
            path="/marketing/surveys/new"
            render={(props) => (
              <SurveyEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/marketing/surveys/:surveyId/view/:teacherKey"
            render={(props) => (
              <SurveyViewerPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/marketing/surveys/:surveyId/edit"
            render={(props) => (
              <SurveyEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/marketing/surveys/:surveyId/results"
            render={(props) => (
              <SurveyResultsPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />

          {/* Recipient lists */}
          <Route
            exact
            path="/marketing/lists/overview"
            render={(props) => (
              <ListOverviewPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            exact
            path="/marketing/lists/new"
            render={(props) => (
              <ListEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
                onAfterSave={this.handleAfterRecipientListSave}
              />
            )}
          />
          {/* <Route
            path="/marketing/lists/:listId/view"
            render={(props) => (
              <SurveyViewerPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/marketing/lists/:listId/edit"
            render={(props) => (
              <SurveyEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/marketing/lists/:listId/results"
            render={(props) => (
              <SurveyResultsPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />           */}

          {/* Mailings */}
          <Route
            exact
            path="/marketing/mailings/overview"
            render={(props) => (
              <MailingOverviewPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            exact
            path="/marketing/mailings/new"
            render={(props) => (
              <MailingEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/marketing/mailings/:mailingId/view"
            render={(props) => (
              <MailingViewerPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          {/*
          <Route
            path="/marketing/mailings/:mailingId/edit"
            render={(props) => (
              <MailingEditorPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
          <Route
            path="/marketing/mailings/:mailingId/results"
            render={(props) => (
              <MailingResultsPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />           */}

          {/* Tools. */}
          <Route
            exact
            path="/tools/miur"
            render={(props) => (
              <MIURToolPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />

          {/* Etc. */}
          <Route
            exact
            path="/etc/projects/:projectName/:submissionId"
            render={(props) => (
              <ClientProjectsPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />

          {/* Overview */}
          <Route
            exact
            path="/"
            render={(props) => (
              <EducationalMainPage {...props}
                user={currUser}
                appData={this.state.appData}
                fetchOrdersReport={this.fetchOrdersReport}
              />
            )}
          />
        </Layout>
      </Router>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js